import { Box, Divider, Flex, Text, UnstyledButton } from '@mantine/core';
import { MarkdownField } from '@refinedev/mantine';
import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { Message } from '../hooks/useChatMessages';
import { camelCaseOrSnakeCaseToWords } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { LoadingAnimation } from './LoadingAnimation';

interface CustomMessageContentProps {
  message: Message;
  isLatestIncoming: boolean;
}

export default function CustomMessageContent({
  message,
  isLatestIncoming,
}: CustomMessageContentProps) {
  const [showThinkingProcess, setShowThinkingProcess] = useState(false);

  const isThinking = isLatestIncoming && message.currentState !== 'ANSWERING';
  const hasThinkingContent = message.thinkingContent.length > 0;
  const hasAnswerContent = message.answerContent.length > 0;

  if (!isLatestIncoming) {
    return (
      <Box
        p="sm"
        bg="white"
        style={{
          borderRadius: '12px 12px 12px 6px',
        }}
      >
        <Box c="gray.10">
          <MarkdownField value={message.answerContent} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p="sm"
      bg={isThinking ? 'transparent' : 'white'}
      style={{
        border: isThinking ? '1px solid var(--mantine-color-gray-2)' : 'none',
        borderRadius: isThinking ? '16px' : '12px 12px 12px 6px',
      }}
    >
      {isThinking && (
        <>
          <Flex justify="start" align="center" mb="xs">
            <LoadingAnimation />
          </Flex>
          <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="#F9510F">
            {camelCaseOrSnakeCaseToWords(message.currentState)}...
          </Text>
          {hasThinkingContent && (
            <Divider style={{ borderColor: 'var(--mantine-color-gray-3)' }} my="xs" />
          )}
        </>
      )}

      {hasThinkingContent && isLatestIncoming && (
        <>
          <UnstyledButton
            onClick={() => setShowThinkingProcess(!showThinkingProcess)}
            mb={showThinkingProcess ? 'xxs' : '0'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--mantine-spacing-xxs)',
            }}
          >
            {showThinkingProcess ? (
              <IconArrowsMinimize size={24} color="black" />
            ) : (
              <IconArrowsMaximize size={24} color="black" />
            )}
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="black">
              {showThinkingProcess ? 'Hide thinking process' : 'View thinking process'}
            </Text>
          </UnstyledButton>

          {showThinkingProcess && (
            <Box c="gray.6">
              <MarkdownField value={message.thinkingContent} />
            </Box>
          )}

          {hasAnswerContent && (
            <Divider style={{ borderColor: 'var(--mantine-color-gray-3)' }} my="xs" />
          )}
        </>
      )}

      {hasAnswerContent && (
        <Box c="gray.10">
          <MarkdownField value={message.answerContent} />
        </Box>
      )}
    </Box>
  );
}
